import type { ConsistencyLinkId, ViewId } from 'balus-common';
import { toast } from 'react-hot-toast';
import type { ViewModelEntity } from '@view-model/domain/view-model';
import type { CommandManager } from '@model-framework/command';
import { ConsistencyLinksOperation } from '@view-model/adapter';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMousetrap } from '@framework/hooks';
import { KeyboardShortcuts } from '@view-model/application/shortcuts';
import { useFollowingUser } from '@view-model/Viewer/useFollowingUser';
interface Props {
  selectedViewIds: Set<ViewId>;
  viewModel: ViewModelEntity;
  selectedConsistencyLinkIds: Set<ConsistencyLinkId>;
  commandManagerRef: React.MutableRefObject<CommandManager>;
}

// ビューモデルで利用できるショートカットを登録する
export function useViewModelPageContentShortcuts(props: Props): void {
  const { t } = useTranslation();
  const propsRef = useRef<Props>(props);
  propsRef.current = props;
  const { selectedViewIds } = props;

  const { commandManagerRef, selectedConsistencyLinkIds, viewModel } = props;
  const { followingUserId, stopFollowing } = useFollowingUser();

  useMousetrap(
    KeyboardShortcuts.undo,
    async () => {
      const commandManager = commandManagerRef.current;
      if (await commandManager.canUndo()) {
        commandManager.undo();
      } else {
        toast.error(t('viewModel.undoRedo.undoFailed'));
      }
    },
    'keydown'
  );

  useMousetrap(
    KeyboardShortcuts.redo,
    async () => {
      const commandManager = commandManagerRef.current;
      if (await commandManager.canRedo()) {
        commandManager.redo();
      } else {
        toast.error(t('viewModel.undoRedo.redoFailed'));
      }
    },
    'keydown'
  );

  useMousetrap(
    KeyboardShortcuts.delete,
    () => {
      // ビューを選択中の場合には、ビューの側でハンドリングしているので、ここでは何もしない
      if (selectedViewIds.size > 0) {
        return;
      }

      // 整合性リンクを選択している場合には、その整合性リンクを削除する
      ConsistencyLinksOperation.handleDelete({
        ids: selectedConsistencyLinkIds,
        viewModelId: viewModel.id,
        commandManager: commandManagerRef.current,
      }).then();
    },
    'keydown'
  );

  // ESCキーでフォローを解除
  useMousetrap(
    KeyboardShortcuts.escape,
    () => {
      if (followingUserId) {
        stopFollowing();
      }
    },
    'keydown'
  );
}
